import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '../ui/button';
import { testimonials } from '../../data/mockData';

const TestimonialsSection = ({ onBookDemo }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="bg-gray-50 py-24" aria-labelledby="testimonials-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <header className="text-center mb-16">
          <span className="text-sm font-medium text-purple-600 mb-3 block">Customers</span>
          <h2 id="testimonials-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Built for teams big and small{' '}
            <br className="hidden sm:block" />
            <span className="text-gray-400">to build AI Agents together</span>
          </h2>
          <p className="text-gray-600 text-lg">
            Hear it directly from our customers.
          </p>
        </header>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto" role="region" aria-label="Customer testimonials carousel">
          <div className="overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-sm p-8 lg:p-12">
            <Quote className="w-12 h-12 text-purple-200 mb-6" aria-hidden="true" />
            
            <div className="relative" aria-live="polite">
              {testimonials.map((testimonial, index) => (
                <figure
                  key={testimonial.id}
                  className={`transition-all duration-500 ${
                    index === currentIndex
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 absolute inset-0 translate-x-8'
                  }`}
                  aria-hidden={index !== currentIndex}
                >
                  <blockquote className="text-xl lg:text-2xl text-gray-700 leading-relaxed mb-8">
                    {testimonial.quote}
                  </blockquote>
                  
                  <figcaption className="flex items-center gap-4">
                    <img
                      src={testimonial.avatar}
                      alt={`${testimonial.author}, ${testimonial.role} at ${testimonial.company}`}
                      className="w-12 h-12 rounded-full object-cover"
                      loading="lazy"
                      width="48"
                      height="48"
                    />
                    <div>
                      <cite className="text-gray-900 font-medium not-italic">{testimonial.author}</cite>
                      <p className="text-gray-500 text-sm">
                        {testimonial.role} at {testimonial.company}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex items-center justify-center gap-4 mt-8" aria-label="Testimonial navigation">
            <button
              onClick={prevSlide}
              className="w-10 h-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors shadow-sm"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>
            
            <div className="flex gap-2" role="tablist" aria-label="Testimonial indicators">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  role="tab"
                  aria-selected={index === currentIndex}
                  aria-label={`Go to testimonial ${index + 1}`}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? 'w-6 bg-gray-900'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={nextSlide}
              className="w-10 h-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors shadow-sm"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>
          </nav>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button
              onClick={onBookDemo}
              className="bg-gray-900 text-white hover:bg-gray-800 font-medium px-6"
              aria-label="Book a demo to see AI AutoBots"
            >
              Book a demo
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
